import _ from "lodash";
import React, { useRef, useEffect, useState } from "react";
import classes from "./GlobalSearchBarV2.scss";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import { withRouter } from "react-router";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { useKey } from "react-use";
import SearchInput from "./SearchInput";
import RecentSearch from "./RecentSearch";
import SearchResultsComp from "./SearchResult";
import ModulesSection from "./ModulesSection";
import Suggestions from "./Suggestions";
import Footer from "./Footer";
import JumpToSection from "./JumpToSection";
import _findIndex from "lodash/findIndex";
import {
  typesConfig,
  getModulesFiltersList,
  getRecentSearchItems,
  getCoursesV2QueryFiltersMemoized,
} from "./Utils";
import useLocalSuggestions from "./useLocalSuggestions";
import { navigate } from "modules/NavigationModule";

import { getUserCoursesV2Query } from "../modules/GlobalSearchBarQueries";
import { getConfigurationQueryEnhancer } from "ChildrenPage/modules/ChildrenPageEnhancers";
import {
  getConfigurationQueryVariable,
  globalSearchConfigurationSelector,
} from "../modules/GlobalSearchBarModule";
import {
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
  updateGlobalSearchCurriculumContext,
} from "modules/Services";
import { recordEvent } from "lib/eventTracker";
import ACLStore from "lib/aclStore";
import { changeLocalSearchStorageData } from "services/localStorage";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { Modal } from "@toddle-edu/ds-web";
import { isTypingInInput } from "Utils";

import { navigateToFeature } from "Courses/components/TeacherCourses/module/Module";
import {
  getFeatureFromKeyboardEvent,
  JUMP_TO_COLUMNS,
  trackJumpToCardClick,
} from "GlobalHeader/modules/Utils";

import { goToCourseRoute } from "GlobalHeader/modules/Module";

const loaderStyles = {
  containerStyle: {
    position: "absolute",
    zIndex: 2,
    height: "100%",
  },
  loadingContainerStyle: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  modalBodyStyle: {
    paddingBottom: 0,
  },
};

/**
 * Here, pseudo interaction keys implies that
 * they don't have any functionality wired to them,
 * however when user presses these key, we don't want to
 * return focus back to search input.
 */

const INTERACTION_KEYS = [
  "ArrowUp",
  "ArrowDown",
  "ArrowRight",
  "ArrowLeft",
  "Enter",
];
const LOCAL_SEARCH_STORAGE_COUNT = 25;
const SEARCH_MODULE_NAME = "searches";

const GlobalSearchBarV2 = props => {
  const {
    showSearchBar,
    toggleGlobalSearchBarModal,
    enableVoiceSearch,
    toggleVoiceSearch,
    userId,
    userInfo,
    navigate,
    searchText,
    module,
    updateGlobalSearchText,
    updateGlobalSearchCoursesContext,
    updateGlobalSearchCurriculumContext,
    t,
    courses,
    shouldUpdateCourses,
    curriculumProgramType,
    orgId,
    curriculumPrograms,
    selectedCurriculum,
    navigateToFeature,
    goToCourseRoute,
    platformCurriculumProgramId,
    modalBodyStyle,
    updateGlobalSearchModuleFilter,
  } = props;

  const {
    localSuggestions,
    isLoading: isLocalSuggestionsLoading,
  } = useLocalSuggestions({
    t,
    module,
    searchText,
    orgId,
    courses,
    curriculumProgramType,
  });

  const searchInputRef = useRef();
  const modalContainerRef = useRef();
  const curriculumDropdownRef = useRef(null);

  const availableShortcutsMapping = useRef({});

  const updateAvailableShortcutsMapping = ({ key, value }) => {
    availableShortcutsMapping.current[key] = value;
  };

  const recentSearchResults = getRecentSearchItems({
    userId,
    module,
    userInfo,
    t,
    selectedCurriculum,
  });

  const modulesList = getModulesFiltersList({
    t,
    userInfo,
    curriculumProgram: selectedCurriculum,
    useV2List: true,
  });

  const [isSearchInputInFocus, setIsSearchInputInFocus] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      let blurTimeout;

      const handleFocus = () => setIsSearchInputInFocus(true);
      // adding the delay here because it should not trigger before onClick of Suggestions/Recent search items
      const handleBlur = () => {
        blurTimeout = setTimeout(() => setIsSearchInputInFocus(false), 500);
      };

      const inputElement = searchInputRef.current;

      if (inputElement) {
        inputElement.addEventListener("focus", handleFocus);
        inputElement.addEventListener("blur", handleBlur);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener("focus", handleFocus);
          inputElement.removeEventListener("blur", handleBlur);
        }
        clearTimeout(blurTimeout);
      };
    }, 0);

    // Cleanup the timeout and reset the search text
    return () => {
      clearTimeout(timer);
      onSearchInputChange();
      updateGlobalSearchModuleFilter(null);
    };
  }, []);

  useEffect(() => {
    if (shouldUpdateCourses) {
      updateGlobalSearchCoursesContext({ courses });
    }
  }, []);

  useEffect(() => {
    let selectedCurriculum = _.find(curriculumPrograms, {
      id: platformCurriculumProgramId,
    });

    if (!selectedCurriculum) {
      selectedCurriculum = _.first(curriculumPrograms);
    }

    updateGlobalSearchCurriculumContext({
      selectedCurriculum,
    });
  }, []);

  const setInputFocus = () => {
    searchInputRef.current?.focus?.();
  };

  const onSearchInputChange = (value = "") => {
    updateGlobalSearchText(value);

    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        searchText: value,
        event_type: "SEARCH",
      },
    });
  };

  const onCurriculumChange = selectedProgram => {
    if (selectedCurriculum?.id === selectedProgram?.id) return;
    // reset available shortcuts mapping when user switches curriculum
    availableShortcutsMapping.current = {};
    updateGlobalSearchCurriculumContext({
      selectedCurriculum: selectedProgram,
    });
  };

  const onSelectModule = ({ type }) => {
    updateGlobalSearchModuleFilter(type);

    setInputFocus();
  };

  const onUnselectModule = () => {
    updateGlobalSearchModuleFilter(null);

    searchText && setInputFocus();
  };

  const onSuggestionClick = ({ item, e }) => {
    const type = item.type;
    // doing this as the item is being stringified in navigate and local storage and the subtitle can be a react element which causes the stringify to fail
    const subTitle = React.isValidElement(item.subTitle) ? "" : item.subTitle;
    const params = {
      ...item,
      type,
      subTitle,
      source: "GLOBAL_SEARCH",
    };
    let shouldNavigate = true;
    const routerParams = props.router.params;

    const isMouseClick = !!e.isTrusted;
    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        clicked_item: type,
        searchText,
        filter_module: module,
        trigger: isMouseClick ? "MOUSE_CLICK" : "KEYBOARD",
      },
    });

    /**
     * @description unit plan details tab data doesn't render
     * when same unit plan is selected again.
     */
    if (
      routerParams?.course_id == item?.metadata?.courseId &&
      routerParams?.unit_id == item?.metadata?.unitPlanId &&
      item.type == typesConfig.UNIT_PLAN.id
    ) {
      shouldNavigate = false;
    }

    toggleGlobalSearchBarModal();
    //navigate to that particular module
    if (shouldNavigate) {
      navigate({ params });
    }
    //change in local storage data
    changeLocalSearchStorageData({
      data: params,
      userId,
      searchModuleName: SEARCH_MODULE_NAME,
      localSearchStorageCount: LOCAL_SEARCH_STORAGE_COUNT,
    });
  };

  const handleEnterKey = e => {
    e.stopPropagation();
    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );

    const curriculumDropdownElement = curriculumDropdownRef?.current;

    const isFocusOnCurriculumDropdown = _.isEqual(
      curriculumDropdownElement,
      currentFocusedElement
    );

    if (isFocusOnCurriculumDropdown) {
      return;
    }

    currentFocusedElement?.click();
    return;
  };

  const focusNextElement = (direction = "up") => e => {
    e.preventDefault();

    const jumpToColumn1Elements = [
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=jump-to-col-0]"
      ) ?? []),
    ];

    const jumpToColumn2Elements = [
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=jump-to-col-1]"
      ) ?? []),
    ];

    const jumpToColumn3Elements = [
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=jump-to-col-2]"
      ) ?? []),
    ];

    const focusableElements = [
      searchInputRef.current,
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=module-filter-item],[data-elm=search-item]"
      ) ?? []),
      ...jumpToColumn1Elements,
      ...jumpToColumn2Elements,
      ...jumpToColumn3Elements,
    ];

    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );
    const currentFocusedElementIndex = _findIndex(
      focusableElements,
      currentFocusedElement
    );

    if (currentFocusedElementIndex === -1 || currentFocusedElement === null) {
      // focus search input if no valid element was already focused
      setInputFocus();
      return;
    }

    let nextFocusElementIndex;
    const lastIndex = focusableElements.length - 1;

    const columnElements = {
      0: jumpToColumn1Elements,
      1: jumpToColumn2Elements,
      2: jumpToColumn3Elements,
    };

    const findElementInColumn = (columnIndex, rowIndex) => {
      const column = columnElements[columnIndex];
      if (!column || column.length === 0) return null;
      return column[rowIndex] ? column[rowIndex] : column[column.length - 1];
    };

    const columnKey = _.findKey(columnElements, elements =>
      _.includes(elements, currentFocusedElement)
    );

    if (direction === "up") {
      nextFocusElementIndex =
        currentFocusedElementIndex === 0 ? -1 : currentFocusedElementIndex - 1;
    } else if (direction === "down") {
      nextFocusElementIndex =
        currentFocusedElementIndex === lastIndex
          ? -1
          : currentFocusedElementIndex + 1;
    } else if (direction === "left" || direction === "right") {
      const columnIdx = parseInt(columnKey, 10);
      const rowIdx = _.findIndex(
        columnElements[columnIdx],
        currentFocusedElement
      );

      let targetColumnIdx = columnIdx;

      // Handles the case if any column is empty
      do {
        if (direction === "left") {
          targetColumnIdx = targetColumnIdx === 0 ? 2 : targetColumnIdx - 1;
        } else if (direction === "right") {
          targetColumnIdx = targetColumnIdx === 2 ? 0 : targetColumnIdx + 1;
        }
      } while (
        columnElements[targetColumnIdx]?.length === 0 &&
        targetColumnIdx !== columnIdx
      );

      if (targetColumnIdx !== columnIdx) {
        const targetElement = findElementInColumn(targetColumnIdx, rowIdx);
        if (targetElement) {
          targetElement.focus();
          setTimeout(() => targetElement.select?.(), 0);
          return;
        }
      }
      return;
    } else {
      console.error("invalid direction", direction);
      return;
    }

    focusableElements[nextFocusElementIndex]?.focus();
    setTimeout(() => {
      // select the texts in the element if the element is input element
      focusableElements[nextFocusElementIndex]?.select?.();
    }, 0);
  };

  const blurCurrentlyFocusedElement = () => {
    modalContainerRef.current
      ?.querySelector(":focus:not(#global-search-input)")
      ?.blur?.();
  };

  // when search text is empty, search text is not in focus and no module is selected
  const shouldShowJumpToSection =
    !isSearchInputInFocus && !searchText && !module;

  const handleKeyDown = e => {
    const key = _.toLower(e.key);
    const isModalOpenCloseTrigger = key === "j";

    if (
      !shouldShowJumpToSection ||
      isModalOpenCloseTrigger ||
      isTypingInInput()
    ) {
      return;
    }

    const shouldFocusInput = key === "/";

    if (shouldFocusInput) {
      setTimeout(() => {
        setInputFocus();
      }, 0);
      return;
    }

    const visibleJumpToFeatures = _.keys(availableShortcutsMapping.current);
    const flattenAvailableShortcutsMapping = _.reduce(
      visibleJumpToFeatures,
      (result, visibleJumpToFeature) => {
        return {
          ...result,
          ...availableShortcutsMapping.current[visibleJumpToFeature],
        };
      },
      {}
    );

    const matchingFeature = getFeatureFromKeyboardEvent({
      e,
      shortcutsMapping: flattenAvailableShortcutsMapping,
    });

    if (matchingFeature) {
      if (_.includes(matchingFeature, "COURSE")) {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.COURSE_FEED,
          featureId: flattenAvailableShortcutsMapping[matchingFeature].courseId,
        });
        goToCourseRoute({
          courseId: flattenAvailableShortcutsMapping[matchingFeature].courseId,
        });
      } else if (_.includes(matchingFeature, "PROJECT")) {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.PROJECTS_FEED,
          featureId:
            flattenAvailableShortcutsMapping[matchingFeature].projectGroupType,
        });
        navigateToFeature({
          feature:
            flattenAvailableShortcutsMapping[matchingFeature].projectGroupType,
          allowCrossCurriculumNavigation: true,
          t,
        });
      } else {
        trackJumpToCardClick({
          e,
          featureType: JUMP_TO_COLUMNS.HOMEPAGE_FEATURES,
          featureId: matchingFeature,
        });
        navigateToFeature({
          feature: matchingFeature,
          allowCrossCurriculumNavigation: true,
          t,
        });
      }
      toggleGlobalSearchBarModal();
    }
  };

  useKey("ArrowUp", focusNextElement("up"), {}, []);
  useKey("ArrowDown", focusNextElement("down"), {}, []);
  useKey("ArrowLeft", focusNextElement("left"), {}, []);
  useKey("ArrowRight", focusNextElement("right"), {}, []);

  useKey("Enter", handleEnterKey, {}, []);
  useKey(
    event => {
      const { key } = event;
      return !_.includes(INTERACTION_KEYS, key);
    },
    handleKeyDown,
    {},
    [shouldShowJumpToSection]
  );

  if (!showSearchBar) {
    return null;
  }

  return (
    <Modal
      dsVersion="2.0"
      width="1008px"
      height="588px"
      isOpen={true}
      onClose={toggleGlobalSearchBarModal}
    >
      <Modal.Body style={modalBodyStyle} isScrollable>
        <div
          ref={modalContainerRef}
          className={classes.container}
          onMouseMove={blurCurrentlyFocusedElement}
        >
          <div className={classes.topContainer}>
            <SearchInput
              onChange={onSearchInputChange}
              module={module}
              onUnselectModule={onUnselectModule}
              enableVoiceSearch={enableVoiceSearch}
              toggleVoiceSearch={toggleVoiceSearch}
              ref={searchInputRef}
              setInputFocus={setInputFocus}
              searchText={searchText}
              curriculumPrograms={curriculumPrograms}
              selectedCurriculum={selectedCurriculum}
              onCurriculumChange={onCurriculumChange}
              curriculumDropdownRef={curriculumDropdownRef}
              platformCurriculumProgramId={platformCurriculumProgramId}
            />
            <ModulesSection
              data={modulesList}
              onSelectModule={onSelectModule}
              onUnselectModule={onUnselectModule}
              selectedModule={module}
            />
          </div>

          <div className={classes.focusZoneContainer}>
            {shouldShowJumpToSection && !_.isEmpty(selectedCurriculum) ? (
              <JumpToSection
                updateAvailableShortcutsMapping={
                  updateAvailableShortcutsMapping
                }
              />
            ) : (
              <>
                {!searchText ? (
                  !module ? (
                    <RecentSearch
                      data={recentSearchResults}
                      module={module}
                      onSuggestionClick={onSuggestionClick}
                    />
                  ) : _.size(recentSearchResults) ? (
                    <RecentSearch
                      data={recentSearchResults}
                      module={module}
                      onSuggestionClick={onSuggestionClick}
                    />
                  ) : (
                    <div className={classes.suggestions}>
                      <Suggestions
                        module={module}
                        onSuggestionClick={onSuggestionClick}
                      />
                    </div>
                  )
                ) : null}

                {searchText ? (
                  <div className={classes.searchResults}>
                    <SearchResultsComp
                      onSuggestionClick={onSuggestionClick}
                      term={searchText}
                      module={module}
                      localSuggestions={localSuggestions}
                      isLoading={isLocalSuggestionsLoading}
                      isData={!isLocalSuggestionsLoading}
                      selectedCurriculum={selectedCurriculum}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <Footer />
      </Modal.Footer>
    </Modal>
  );
};

const isGlobalSearchCourseExpired = updatedAt => {
  const tenMinuteMilliSeconds = 1000 * 60 * 30;
  const diff = Date.now() - updatedAt;
  return diff > tenMinuteMilliSeconds;
};

const getUserCurriculumCourses = ({ courses, curriculumIds = [] }) => {
  if (_.isEmpty(curriculumIds)) {
    return courses;
  }

  const filteredCourses = _.filter(courses, course => {
    const courseCurriculumId = _.get(course, "curriculumProgram.id", null);
    return _.includes(curriculumIds, courseCurriculumId);
  });

  return filteredCourses;
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const {
    id: userId,
    userEntityType,
    org_id: orgId,
    userCurriculumPrograms: curriculumPrograms,
  } = userInfo;
  const curriculumProgramType = _.uniq(_.map(curriculumPrograms, "type"));
  const targetCurriculumIds = _.map(curriculumPrograms, "id");

  const platformCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );

  const searchText = _.get(
    state,
    "app_services.globalSearchBar.searchText",
    ""
  );

  const showSearchBar = _.get(
    state,
    "app_services.globalSearchBar.showSearchBar",
    false
  );
  const enableVoiceSearch = _.get(
    state,
    "app_services.globalSearchBar.enableVoiceSearch",
    false
  );
  const globalSearchCourses = _.get(
    state,
    "app_services.globalSearchBar.courses.allCourses"
  );
  const globalSearchCourseUpdatedAt = _.get(
    state,
    "app_services.globalSearchBar.courses.updatedAt",
    null
  );

  const module = _.get(state, "app_services.globalSearchBar.module", null);

  const shouldUpdateCourses =
    globalSearchCourses === null ||
    globalSearchCourseUpdatedAt === null ||
    isGlobalSearchCourseExpired(globalSearchCourseUpdatedAt);
  const configurationQueryVariables = getConfigurationQueryVariable(state);

  const userCourses = getUserCurriculumCourses({
    courses: globalSearchCourses,
    curriculumIds: targetCurriculumIds,
  });

  const selectedCurriculum = _.get(
    state,
    "app_services.globalSearchBar.selectedCurriculum",
    ""
  );

  const userCurriculumProgramsIds = _.map(curriculumPrograms, "id");

  const coursesV2QueryFilters = getCoursesV2QueryFiltersMemoized({
    userId,
    userCurriculumProgramsIds,
  });

  return {
    accessPermissions: [
      "TeacherPortal:GlobalSearchBar",
      "StudentPortal:GlobalSearchBar",
    ],
    errorText: "Do not have permission to see this page",
    showErrorComp: false,
    orgId,
    userId,
    userEntityType,
    userInfo,
    searchText,
    showSearchBar,
    enableVoiceSearch,
    module,
    shouldUpdateCourses,
    configurationQueryVariables,
    courses: userCourses,
    curriculumProgramType,
    isData: true,
    isLoading: false,
    selectedCurriculum,
    platformCurriculumProgramId,
    ...loaderStyles,
    coursesV2QueryFilters,
  };
};

const mapActionCreators = {
  navigate,
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
  updateGlobalSearchCurriculumContext,
  navigateToFeature,
  goToCourseRoute,
};

export default compose(
  withRouter,
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCoursesV2Query, {
    name: "getUserCoursesV2Query",
    skip: ({ accessPermissions, shouldUpdateCourses }) => {
      return !ACLStore.can(accessPermissions) || !shouldUpdateCourses;
    },
    options: ({ coursesV2QueryFilters }) => ({
      variables: coursesV2QueryFilters,
      fetchPolicy: "cache-and-network",
    }),
    props: ({ getUserCoursesV2Query, ownProps: { isData, isLoading } }) => {
      const queryResult = _.get(getUserCoursesV2Query, "node", {});
      const courseEdges = _.get(queryResult, "coursesV2.edges", []);
      const courses = _.map(courseEdges, "node");
      const networkStatus = _.get(getUserCoursesV2Query, "networkStatus");
      return {
        courses,
        isData: !_.isEmpty(queryResult) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  getConfigurationQueryEnhancer(globalSearchConfigurationSelector, {
    fetchPolicy: "cache-and-network",
  }),
  withCombinedAccessBlocker,
  withLoader
)(GlobalSearchBarV2);
